import { decode } from "jsonwebtoken";

export enum SYSTEM {
  DEVELOPER = "b2012b16-f7c1-4628-84e9-70319c6eb3ef",
  ADMIN = "f8c04a6e-e90c-4e89-a8a6-aac6e5af4948"
}

export enum TRAINERS {
  TRAINER = "fb2d262c-0928-4744-b58a-90617e161121",
  ASSISTANT = "0d36bc6c-8475-4390-a657-46f32eadbe67"
}

enum VETERINARIANS {
  SENIOR = "0aefecdd-da4d-42f9-84fb-434b852b021f",
  CHIEF = "c315df3f-37db-4fbc-b2e1-c8778d5d291c",
  TRACK = "f3e22769-e6df-4e5d-a164-31a5019519e3",
  REGULATORY = "e8fd631d-3ae1-44b7-b20b-05a388b2350a"
}

export enum MAINTENANCE {
  SUPERVISOR = "7c0495d7-710f-4a76-b323-b06d4328cb0d",
  STAFF = "4b9b9a1e-04bf-45d1-b03c-d7966c613e70"
}

enum OFFICIALS {
  RACING_SECRETARY = "2593d685-4c06-4f5a-becd-4e9c40744f2f"
}

interface IUserAccessToken {
  sub: string;
  // Uuid of the RH360 user on API Resources
  ["https://api.1stbet.com/rh360_user_id"]: string;
  // Roles
  ["https://api.1stbet.com/rh360_user_roles"]: UserAuthRole[];
}

export interface UserAuthRole {
  id: string;
  name: string;
  facilityId: string | undefined;
  trainerId: string | undefined;
}

export class AccessTokenHelper {
  public rh360Id: string;
  public roles: UserAuthRole[];
  public isLoaded: boolean;

  public constructor(token: string | null) {
    if (token) {
      const claims = decode(token) as unknown as Partial<IUserAccessToken>;

      this.roles = claims["https://api.1stbet.com/rh360_user_roles"] || [];
      this.rh360Id = claims["https://api.1stbet.com/rh360_user_id"];
      this.isLoaded = true;
    } else {
      this.roles = [];
      this.rh360Id = "";
      this.isLoaded = false;
    }
  }

  public get isAdmin(): boolean {
    return Boolean(this.roles.some(r => r.id === SYSTEM.ADMIN));
  }

  public get isSystemDeveloper(): boolean {
    return Boolean(this.roles.some(r => r.id === SYSTEM.DEVELOPER));
  }

  public get isMaintenance(): boolean {
    return Boolean(
      this.roles.some(r => Object.values<string>(MAINTENANCE).includes(r.id))
    );
  }

  public get isMaintenanceSupervisor(): boolean {
    return Boolean(this.roles.some(r => r.id === MAINTENANCE.SUPERVISOR));
  }

  public get isMaintenanceStaff(): boolean {
    return Boolean(this.roles.some(r => r.id === MAINTENANCE.STAFF));
  }

  public get isRacingOfficial(): boolean {
    return Boolean(this.roles.some(r => r.id === OFFICIALS.RACING_SECRETARY));
  }

  public get isTrainer(): boolean {
    return Boolean(
      this.roles.some(r => Object.values<string>(TRAINERS).includes(r.id))
    );
  }

  public get isAssistantTrainer(): boolean {
    return Boolean(this.roles.some(r => r.id === TRAINERS.ASSISTANT));
  }

  public get isVeterinarian(): boolean {
    return Boolean(
      this.roles.some(r => Object.values<string>(VETERINARIANS).includes(r.id))
    );
  }

  public get isChiefVet(): boolean {
    return Boolean(this.roles.some(r => r.id === VETERINARIANS.CHIEF));
  }

  public get isSeniorVet(): boolean {
    return Boolean(this.roles.some(r => r.id === VETERINARIANS.SENIOR));
  }

  public get isTrackVet(): boolean {
    return Boolean(this.roles.some(r => r.id === VETERINARIANS.TRACK));
  }

  public get isRegulatoryVet(): boolean {
    return Boolean(this.roles.some(r => r.id === VETERINARIANS.REGULATORY));
  }

  public get racingOfficialFacilityIds(): string[] {
    return this.roles
      .filter(r => r.id === OFFICIALS.RACING_SECRETARY)
      .map(r => r.facilityId);
  }

  public get maintenanceFacilityIds(): string[] {
    return this.roles
      .filter(r => Object.values<string>(MAINTENANCE).includes(r.id))
      .map(r => r.facilityId);
  }

  public get facilityIdsListFromRoles(): string[] {
    return this.roles.filter(r => !!r.facilityId).map(r => r.facilityId);
  }

  public get trainerId(): string {
    return this.roles.find(r => r.name.toLowerCase().includes("trainer"))
      ?.trainerId;
  }

  public get trainerIds(): string[] {
    return this.roles.filter(r => !!r.trainerId).map(r => r.trainerId);
  }

  public get vetFacilityIds(): string[] {
    return this.roles
      .filter(
        r => r.name.toLowerCase().includes("veterinarian") && !!r.facilityId
      )
      .map(r => r.facilityId);
  }

  // A valid filter for filtering horses or workout_requests by those that should
  // be workable (workout_request:request) by this user
  public get trainerHorseAndWorkoutFilter(): {
    facilityIds?: string[];
    trainerIds?: string[];
  } {
    // A racing secretary filters horses and workout requests by facility
    const racingSecretaryFacilityIds = this.roles
      .filter(r => r.id === OFFICIALS.RACING_SECRETARY)
      .map(r => r.facilityId);

    if (racingSecretaryFacilityIds.length > 0) {
      return {
        facilityIds: racingSecretaryFacilityIds
      };
    } else {
      return {
        trainerIds: this.trainerIds
      };
    }
  }

  public hasRole(roleId: string): boolean {
    return this.roles.some(r => r.id === roleId);
  }

  public hasRoleAtFacility(roleId: string, facilityId?: string): boolean {
    return this.roles.some(
      r => r.id === roleId && (facilityId ? r.facilityId === facilityId : true)
    );
  }

  public hasRoleForTrainer(roleId: string, trainerId?: string): boolean {
    return this.roles.some(
      r => r.id === roleId && (trainerId ? r.trainerId === trainerId : true)
    );
  }
}
